import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { cx } from '@emotion/css';
import type { YMapLocationRequest } from '@yandex/ymaps3-types';

import { EcpMapProps } from './ecp-map.models';
import { v2025PP18603 } from '../../../../constants/veeva';
import { CHILD_ZOOM, LIST_ZOOM, MAP_OPTIONS, AUTOSUGGEST_ZOOM } from '../../../../constants/ecp-locator';
import { useStyles } from './ecp-map.styles';
import { IEcpStore } from '../../../../rest/ecp-locator';
import { getLatLng } from '../../../../utils/ecp-locator';
import { boundsFromFeatures, safeCenterFrom } from '../../../../utils/latlng/latlng';
import {
  setUserLocation,
  setStoreDetail,
  setMarkerCode,
  setSearchBounds,
} from '../../../../graphql/ecp-locator/ecp-locator.cache';
import { scrollToTop } from '../../../../utils/scroll-to-top';
import { useYandexMapsScript } from '../../../../hooks/useYandexMapsScripts';
import { MapPlaceholder } from '../../../common/map-placeholder/map-placeholder';
import { initializeYandexMaps, cleanupYandexMaps } from '../../../../lib/ymap';
import { MarkerCluster } from '../../../common/marker-cluster';
import { useMapStyles } from '../../../../hooks/use-map-styles/use-map-styles';
import { pushEcpLocatorEvent } from '../../../../utils/analytics/push-ecp-locator-event';
import { EcpLocatorEvent } from '../../../../models/ya-metrica-events';

type GeoJSONFeature = {
  type: 'Feature';
  id: string;
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
};

const DEFAULT_CENTER: [number, number] = [37.618423, 55.751244];
const DEFAULT_ZOOM = LIST_ZOOM ?? 9;

const CAMERA_ANIM: Partial<YMapLocationRequest> = { duration: 700 };

const YANDEX_MAPS_API_KEY = process.env.REACT_APP_YANDEX_MAPS_AK || '';

export const EcpMap: FC<EcpMapProps> = ({
  ecpStores,
  storeDetail,
  isMapHidden,
  mapWrapperStyles,
  isControlsHidden,
  isMarkersHidden,
  ...props
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { isLoaded, isError, errorMessage } = useYandexMapsScript(YANDEX_MAPS_API_KEY);

  const [mapComponents, setMapComponents] = useState<any>(null);

  const userLocation = useReactiveVar(setUserLocation);
  const markerCode = useReactiveVar(setMarkerCode);
  const searchBounds = useReactiveVar(setSearchBounds);
  const prevUserLocationRef = useRef<typeof userLocation | null>(null);


  const initialCenter = useMemo(
    () => safeCenterFrom(getLatLng(userLocation)) ?? DEFAULT_CENTER,
    [userLocation],
  );

  const [location, setLocation] = useState<YMapLocationRequest>({
    center: initialCenter,
    zoom: DEFAULT_ZOOM,
  });

  const { mapStyles } = useMapStyles();

  useEffect(() => {
    if (isLoaded && !mapComponents) {
      initializeYandexMaps()
        .then((modules) => {
          setMapComponents(modules);
        })
        .catch((error) => {
          console.error('Failed to initialize Yandex Maps:', error);
        });
    }
  }, [isLoaded, mapComponents]);

  useEffect(() => {
    return () => {
      cleanupYandexMaps();
    };
  }, []);

  useEffect(() => {
    if (storeDetail) {
      const storeCenter = safeCenterFrom(getLatLng(storeDetail));
      if (storeCenter) {
        setLocation({
          center: storeCenter,
          zoom: CHILD_ZOOM,
          ...CAMERA_ANIM,
        });
      }
      setMarkerCode(storeDetail.code || '');
      return;
    }

    const defaultCenter = safeCenterFrom(getLatLng(userLocation));
    if (defaultCenter) {
      setLocation({
        center: defaultCenter,
        zoom: DEFAULT_ZOOM,
        ...CAMERA_ANIM,
      });
    }
    setMarkerCode('');
  }, [storeDetail]);

  useEffect(() => {
    if (storeDetail) return;
    const c = safeCenterFrom(getLatLng(userLocation));
    if (!c) return;

    const prevLocation = prevUserLocationRef.current;
    const isNewLocation =
    prevLocation?.latitude !== userLocation?.latitude ||
    prevLocation?.longitude !== userLocation?.longitude;

    prevUserLocationRef.current = userLocation;

    if (isNewLocation && prevLocation !== null) {
      if (searchBounds) {
        setLocation({
          bounds: searchBounds,
          duration: 700,
        });
        setSearchBounds(null);
      } else {
        setLocation({
          center: c,
          zoom: AUTOSUGGEST_ZOOM,
          duration: 700,
        });
      }
    } else if (prevLocation === null) {
      setLocation({
        center: c,
        zoom: DEFAULT_ZOOM,
      });
    }
  }, [userLocation, storeDetail, searchBounds]);

  useEffect(() => {
    if (isMobile && !isMapHidden) scrollToTop();
  }, [isMobile, isMapHidden]);

  const onMarkerClick = useCallback((store: IEcpStore) => {
    setMarkerCode(store.code);
    setStoreDetail(store);

    const c = safeCenterFrom(getLatLng(store));
    if (c) {
      setLocation({
        center: c,
        zoom: CHILD_ZOOM,
        ...CAMERA_ANIM,
      });
    }

    pushEcpLocatorEvent(EcpLocatorEvent.MapIcon, store.code || '');
  }, []);

  const handleClusterClick = useCallback((features: GeoJSONFeature[]) => {
    if (!features?.length) return;
    try {
      const bounds = boundsFromFeatures(features);
      setLocation({
        bounds,
        ...CAMERA_ANIM,
      });
    } catch (error) {
      console.warn('Error handling cluster click:', error);
    }
  }, []);

  const showLoading = (!isLoaded && !isError) || (!mapComponents && isLoaded);
  const showError = isError;

  if (showLoading || showError) {
    return (
      <div className={cx(classes.mapWrapper, mapWrapperStyles)} data-testid="yandex-map-wrapper">
        {showLoading && <MapPlaceholder loading />}
        {showError && <MapPlaceholder error message={errorMessage || 'Failed to load map'} />}
      </div>
    );
  }

  const {
    YMap,
    YMapDefaultSchemeLayer,
    YMapDefaultFeaturesLayer,
    YMapControls,
    YMapZoomControl,
  } = mapComponents;

  return (
    <div className={cx(classes.mapWrapper, mapWrapperStyles)} data-testid="yandex-map-wrapper">
      <YMap location={location} mode="vector" {...MAP_OPTIONS} {...props}>
        <YMapDefaultSchemeLayer customization={mapStyles} />
        <YMapDefaultFeaturesLayer />
        {!isMarkersHidden && (
          <MarkerCluster
            ecpStores={ecpStores}
            onMarkerClick={(_e, store) => onMarkerClick(store)}
            onClusterClick={handleClusterClick}
            markerCode={markerCode}
            ymapComponents={mapComponents}
          />
        )}
        {!isControlsHidden && (
          <YMapControls orientation="vertical" position="right">
            <YMapZoomControl />
          </YMapControls>
        )}
      </YMap>
      <div className={classes.veevaNumber}>{v2025PP18603}</div>
    </div>
  );
};
